import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { motion } from "framer-motion";
import { Search, ChevronDown, ChevronUp, Lock, LockOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  terjangkau: {
    label: "Terjangkau",
    cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
    dot: "bg-emerald-400",
    color: "#10B981",
  },
  segera: {
    label: "Segera Hadir",
    cls: "border-amber-400/40 bg-amber-400/10 text-amber-300",
    dot: "bg-amber-400",
    color: "#F59E0B",
  },
  belum: {
    label: "Belum Tersedia",
    cls: "border-white/10 bg-white/5 text-slate-500",
    dot: "bg-slate-500",
    color: "#64748B",
  },
};

const MAX_VISIBLE = 6;

export default function Coverage() {
  const [areas, setAreas] = useState([]);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [mapLocked, setMapLocked] = useState(true);
  const mapRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    if (mapLocked) {
      m.dragging.disable();
      m.touchZoom.disable();
      m.doubleClickZoom.disable();
    } else {
      m.dragging.enable();
      m.touchZoom.enable();
      m.doubleClickZoom.enable();
    }
  }, [mapLocked, loading]);

  useEffect(() => {
    fetch(`${API}/coverage`)
      .then((r) => r.json())
      .then((d) => setAreas(d.areas || []))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () =>
      areas.filter((a) =>
        `${a.name} ${a.city}`.toLowerCase().includes(q.trim().toLowerCase())
      ),
    [areas, q]
  );
  const covered = areas.filter((a) => a.status === "terjangkau").length;
  const searching = q.trim().length > 0;
  const visible = searching || expanded ? filtered : filtered.slice(0, MAX_VISIBLE);

  return (
    <section id="jangkauan" data-testid="coverage-section" className="relative pb-6 pt-4 sm:pb-8 sm:pt-6">
      <div className="pointer-events-none absolute left-0 top-1/3 h-[360px] w-[360px] rounded-full bg-[#1E5AA8]/15 blur-[140px]" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          className="font-mono text-xs tracking-[0.25em] text-cyan-400"
        >
          CEK JANGKAUAN
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ delay: 0.1 }}
          className="mt-4 font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl"
        >
          Rumahmu sudah terjangkau?
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ delay: 0.18 }}
          className="mt-4 max-w-xl text-base text-slate-300"
        >
          Ketik nama kecamatan atau kota/kabupaten Anda, atau klik titik zona di peta untuk
          melihat status jaringan Fasnet Fiber di area tersebut.
        </motion.p>

        {!loading && !err && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ delay: 0.2 }}
          >
            <div
              data-testid="coverage-map"
              className="relative mt-10 overflow-hidden rounded-3xl border border-white/10"
            >
              <MapContainer
                ref={mapRef}
                center={[-0.15, 110.1]}
                zoom={7}
                scrollWheelZoom={false}
                dragging={false}
                touchZoom={false}
                doubleClickZoom={false}
                className="h-[320px] w-full sm:h-[420px]"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {areas
                  .filter((a) => a.lat != null && a.lng != null)
                  .map((a) => {
                    const s = STATUS[a.status] || STATUS.belum;
                    const coveredOne = a.status === "terjangkau";
                    return (
                      <CircleMarker
                        key={`${a.city}-${a.name}`}
                        center={[a.lat, a.lng]}
                        radius={coveredOne ? 12 : 8}
                        pathOptions={{
                          color: s.color,
                          fillColor: s.color,
                          fillOpacity: coveredOne ? 0.4 : 0.22,
                          weight: 2,
                        }}
                        eventHandlers={{ click: () => setQ(a.name) }}
                      >
                        <Popup>
                          <strong>{a.name}</strong>
                          <br />
                          {a.city} — {s.label}
                        </Popup>
                      </CircleMarker>
                    );
                  })}
              </MapContainer>
              <button
                data-testid="map-lock-toggle"
                onClick={() => setMapLocked((v) => !v)}
                className={`absolute right-3 top-3 z-[1000] inline-flex items-center gap-1.5 rounded-full border px-3 py-2 font-mono text-[11px] font-bold backdrop-blur-md transition-colors duration-200 ${
                  mapLocked
                    ? "border-white/20 bg-[#050914]/80 text-slate-300 hover:text-white"
                    : "border-emerald-400/50 bg-emerald-400/15 text-emerald-300"
                }`}
              >
                {mapLocked ? (
                  <>
                    <Lock className="h-3.5 w-3.5" /> Peta Terkunci
                  </>
                ) : (
                  <>
                    <LockOpen className="h-3.5 w-3.5" /> Geser Peta Aktif
                  </>
                )}
              </button>
            </div>
            <div
              data-testid="coverage-legend"
              className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-400"
            >
              {Object.entries(STATUS).map(([k, s]) => (
                <span key={k} className="inline-flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                  {s.label}
                </span>
              ))}
              <span className="font-mono text-slate-500 sm:ml-auto">
                Klik titik di peta untuk memfilter
              </span>
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ delay: 0.25 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <div className="relative w-full max-w-md flex-1" style={{ minWidth: 240 }}>
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="coverage-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="contoh: Tayan Hilir, Mandor, Sanggau..."
              className="w-full rounded-full border border-white/10 bg-[#0A1124]/80 py-3 pl-11 pr-4 text-sm text-white transition-colors duration-200 placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none"
            />
          </div>
          <p data-testid="coverage-summary" className="font-mono text-xs text-slate-400">
            <span className="font-bold text-emerald-400">{covered}</span> / {areas.length}{" "}
            kecamatan terjangkau
          </p>
        </motion.div>

        {loading ? (
          <p data-testid="coverage-loading" className="mt-10 font-mono text-sm text-slate-500">
            Memuat data jangkauan...
          </p>
        ) : err ? (
          <p data-testid="coverage-error" className="mt-10 font-mono text-sm text-red-400">
            Gagal memuat data jangkauan. Coba muat ulang halaman.
          </p>
        ) : (
          <>
            <motion.div
              layout
              data-testid="coverage-grid"
              className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
            >
              {visible.map((a) => {
                const s = STATUS[a.status] || STATUS.belum;
                const slug = a.name.toLowerCase().replace(/\s+/g, "-");
                return (
                  <div
                    key={`${a.city}-${a.name}`}
                    data-testid={`coverage-item-${slug}`}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-[#0A1124]/70 px-4 py-3.5"
                  >
                    <div>
                      <p className="text-sm font-bold text-white">{a.name}</p>
                      <p className="text-xs text-slate-500">{a.city}</p>
                    </div>
                    <span
                      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold ${s.cls}`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
                      {s.label}
                    </span>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <p
                  data-testid="coverage-empty"
                  className="col-span-full rounded-2xl border border-white/10 bg-[#0A1124]/70 px-4 py-6 text-center text-sm text-slate-400"
                >
                  Kecamatan tidak ditemukan. Tanya CS kami untuk info jangkauan terbaru.
                </p>
              )}
            </motion.div>

            {!searching && filtered.length > MAX_VISIBLE && (
              <div className="mt-6 flex justify-center">
                <button
                  data-testid="coverage-toggle-btn"
                  onClick={() => setExpanded((v) => !v)}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-bold text-slate-300 transition-colors duration-200 hover:border-cyan-400/50 hover:text-white"
                >
                  {expanded ? (
                    <>
                      <ChevronUp className="h-4 w-4" /> Sembunyikan
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" /> Selanjutnya — {filtered.length - MAX_VISIBLE} Kecamatan Lainnya
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
